import React from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { 
  ChatBubbleLeftRightIcon, 
  ChatBubbleLeftIcon,
  HeartIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';

/**
 * QuestionActivityItem component for displaying a user's forum question
 * Shows question title, content preview, timestamp, and engagement metrics
 */
const QuestionActivityItem = React.memo(({ question }) => {
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {
      return '';
    }
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    const plainText = text.replace(/<[^>]*>/g, ''); // Remove HTML tags
    if (plainText.length <= maxLength) return plainText;
    return plainText.substring(0, maxLength).trim() + '...';
  };

  return (
    <div className="flex items-start space-x-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 hover:shadow-sm">
      {/* Question Icon */}
      <div className="flex-shrink-0 mt-0.5">
        <ChatBubbleLeftRightIcon className="w-4 h-4 text-purple-500" />
      </div>

      {/* Question Content */}
      <div className="flex-1 min-w-0">
        {/* Activity Type and Timestamp */}
        <div className="flex items-center space-x-2 mb-1">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
            Asked
          </span>
          <span className="text-xs text-gray-400 dark:text-gray-500">
            {formatDate(question.createdAt)}
          </span>
        </div>

        {/* Question Title and Content */}
        <Link
          to={`/forum/${question.id}`}
          className="block hover:text-purple-600 dark:hover:text-purple-400 transition-colors duration-200"
        >
          <h4 className="text-sm font-medium text-gray-900 dark:text-white line-clamp-1 mb-1">
            {question.title}
          </h4>
          {question.content && (
            <p className="text-xs text-gray-600 dark:text-gray-300 line-clamp-2">
              {truncateText(question.content)}
            </p>
          )}
        </Link>

        {/* Engagement Metrics */}
        <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
          {/* View Count */}
          <div className="flex items-center space-x-1">
            <EyeIcon className="w-3 h-3" />
            <span>{question.viewCount || 0}</span>
          </div>

          {/* Reply Count */}
          <div className="flex items-center space-x-1">
            <ChatBubbleLeftIcon className="w-3 h-3" />
            <span>{question.replyCount || 0}</span>
          </div>

          {/* Like Count */}
          <div className="flex items-center space-x-1">
            {question.likeCount > 0 ? (
              <HeartIconSolid className="w-3 h-3 text-red-500" />
            ) : (
              <HeartIcon className="w-3 h-3" />
            )}
            <span>{question.likeCount || 0}</span>
          </div>
        </div>
      </div>
    </div>
  );
});

QuestionActivityItem.displayName = 'QuestionActivityItem';

export default QuestionActivityItem;